/**
 * Invoice service description — the «Найменування робіт, послуг» line that goes
 * on every рахунок and акт.
 *
 * Ukrainian counterparties' accountants expect wording that matches the КВЕД the
 * ФОП is registered under, not the internal service name ("Spinhouse Animation
 * General Plan"), so managers were pasting the legal formula into each document
 * by hand. This is that formula, stored once and applied automatically.
 * backlog 109b77cc.
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { FileSignature, Save, Languages, Hash } from "lucide-react";

const InvoiceDescriptionSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    ua: "",
    en: "",
    append_document_number: false,
    note: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/settings/invoice-service-description");
        setSettings({
          enabled: !!res.data.enabled,
          ua: res.data.ua || "",
          en: res.data.en || "",
          append_document_number: !!res.data.append_document_number,
          note: res.data.note || "",
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/invoice-service-description", settings);
      toast.success("Saved — applies to documents generated from now on");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  const preview = (settings.ua || "—") + (settings.append_document_number ? ", 2026-00245" : "");

  return (
    <div data-testid="invoice-description-settings-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
            <FileSignature className="h-7 w-7 text-orange-500" />
            Invoice service description
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            The «Найменування робіт, послуг» line printed on every рахунок and акт.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">
          <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <Card className={settings.enabled ? "bg-orange-950/20 border-orange-900/40" : "bg-zinc-900/50 border-zinc-800"}>
        <CardContent className="p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-zinc-50">Use one standard wording</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {settings.enabled
                ? "On — every new рахунок and акт prints the text below, whatever the service is called internally."
                : "Off — each document falls back to the per-service-type wording built into the portal."}
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(v) => setSettings({ ...settings, enabled: !!v })}
            data-testid="invoice-desc-enabled"
          />
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold text-zinc-50 flex items-center gap-2">
            <Languages className="h-4 w-4 text-orange-500" /> Wording
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <Label className="text-zinc-300">Ukrainian (рахунок / акт in ₴)</Label>
            <Textarea
              value={settings.ua}
              onChange={(e) => setSettings({ ...settings, ua: e.target.value })}
              rows={4}
              className="bg-zinc-950 border-zinc-800 text-zinc-50"
              data-testid="invoice-desc-ua"
            />
            <p className="text-xs text-zinc-500">
              Keep it aligned with the ФОП's registered КВЕД — this is the text the client's accountant reads.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">English (invoice / act in $ or €)</Label>
            <Textarea
              value={settings.en}
              onChange={(e) => setSettings({ ...settings, en: e.target.value })}
              rows={4}
              className="bg-zinc-950 border-zinc-800 text-zinc-50"
              data-testid="invoice-desc-en"
            />
          </div>

          <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-zinc-800 bg-zinc-950/50">
            <div className="flex items-start gap-3">
              <Hash className="h-4 w-4 text-zinc-500 mt-0.5" />
              <div>
                <p className="text-sm text-zinc-200">Append the document number</p>
                <p className="text-xs text-zinc-500">
                  Adds «, 2026-00245» to the line. Some accountants ask for it to tie акт to рахунок.
                </p>
              </div>
            </div>
            <Switch
              checked={settings.append_document_number}
              onCheckedChange={(v) => setSettings({ ...settings, append_document_number: !!v })}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-zinc-300">Preview</Label>
            <div className="p-3 rounded-lg border border-zinc-800 bg-zinc-950 text-sm text-zinc-300">
              {preview}
            </div>
            <p className="text-xs text-zinc-500">
              Documents already issued keep the wording they were generated with — editing this never rewrites
              a рахунок the client already has.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceDescriptionSettingsPage;
